import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Megaphone, Plus, RefreshCw, Pencil, Power, Trash2, ListChecks, XCircle,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import PromotionEditDialog from '../components/admin/PromotionEditDialog';
import promotionsService from '../services/promotionsService';
import { invalidatePromotionCache } from '../hooks/usePromotion';
import { Promotion } from '../types/promotion';
import { useAuth } from '../context/AuthContext';
import { CHIP_PILL } from '../utils/chipColors';

type StatusBadge = 'Active' | 'Scheduled' | 'Ended' | 'Disabled';

function statusFor(p: Promotion, now: Date = new Date()): StatusBadge {
  if (!p.is_active) return 'Disabled';
  const start = p.start_at ? new Date(p.start_at) : null;
  const end = p.end_at ? new Date(p.end_at) : null;
  if (start && now < start) return 'Scheduled';
  if (end && now > end) return 'Ended';
  return 'Active';
}

// Promotion lifecycle pills sourced from canonical CHIP_PILL (Family B —
// bg-50 + ring + text-700). Pre-Phase-0 these were inline bg-50 + text-700
// with NO ring; the unified version adds a 1px ring matching every other
// Family B pill in the app (LeadStatusPill, ReconciliationStatusPill).
// Active uses HEALTHY (green) rather than the pre-Phase-0 emerald — within
// the green family, slightly less saturated; consistent with the canonical
// "healthy / running" semantics across the app.
const STATUS_CLASSES: Record<StatusBadge, string> = {
  Active:    CHIP_PILL.HEALTHY,
  Scheduled: CHIP_PILL.ACCENT,
  Ended:     CHIP_PILL.NEUTRAL,
  Disabled:  CHIP_PILL.ATTENTION,
};

const PromotionsAdminPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const [promos, setPromos] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPromos(await promotionsService.listPromotions());
    } catch (err: any) {
      setError(err?.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSaved = (saved: Promotion) => {
    setPromos((prev) => {
      const idx = prev.findIndex((p) => p.promo_id === saved.promo_id);
      if (idx >= 0) {
        const next = [...prev];
        // Preserve lead_count — create/update endpoints don't enrich it.
        next[idx] = { ...next[idx], ...saved, lead_count: next[idx].lead_count };
        return next;
      }
      return [{ ...saved, lead_count: 0 }, ...prev];
    });
    setEditing(null);
    setCreatingNew(false);
    invalidatePromotionCache();
  };

  const handleToggle = async (p: Promotion) => {
    try {
      const updated = await promotionsService.updatePromotion(p.promo_id, { ...p, is_active: !p.is_active });
      // Preserve lead_count from local state — the update endpoint doesn't enrich it.
      setPromos((prev) => prev.map((x) => (
        x.promo_id === updated.promo_id ? { ...x, ...updated, lead_count: x.lead_count } : x
      )));
      invalidatePromotionCache();
    } catch (err: any) {
      alert(err?.message || 'Failed to toggle');
    }
  };

  const handleDelete = async (p: Promotion) => {
    if (!window.confirm(`Delete promotion "${p.title}"? This cannot be undone.`)) return;
    try {
      await promotionsService.deletePromotion(p.promo_id);
      setPromos((prev) => prev.filter((x) => x.promo_id !== p.promo_id));
      invalidatePromotionCache();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete');
    }
  };

  const sorted = useMemo(
    () => [...promos].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [promos],
  );

  return (
    <StandardLayout>
      <PageHeader
        title="Promotions"
        pageType="management"
        icon={<Megaphone className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Listings' },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => setCreatingNew(true)}
              leftIcon={<Plus className="h-4 w-4" />}
              disabled={!isAdmin}
              className="w-full sm:w-auto"
            >
              New Promotion
            </Button>
            <Button
              variant="outline"
              onClick={fetch}
              leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Refresh
            </Button>
          </div>
        }
      />

      {!isAdmin && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-800 dark:text-red-200 font-medium">Access Denied</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">This page is only accessible to administrators.</p>
        </div>
      )}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Promotions ({sorted.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="px-6 py-8">
                <LoadingSpinner message="Loading promotions..." />
              </div>
            )}
            {error && (
              <div className="px-6 py-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-red-800 dark:text-red-200">{error}</span>
                </div>
              </div>
            )}
            {!loading && !error && sorted.length === 0 && (
              <div className="text-center py-12 px-6">
                <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No Promotions</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Create your first promotion to start capturing leads.</p>
                <Button variant="primary" onClick={() => setCreatingNew(true)} leftIcon={<Plus className="h-4 w-4" />}>
                  New Promotion
                </Button>
              </div>
            )}
            {!loading && !error && sorted.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tenants</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Surfaces</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Window</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Leads</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sorted.map((p) => {
                      const status = statusFor(p);
                      const window = [
                        p.start_at ? new Date(p.start_at).toLocaleDateString() : '—',
                        p.end_at ? new Date(p.end_at).toLocaleDateString() : '—',
                      ].join(' → ');
                      return (
                        <tr key={p.promo_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{p.title}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{p.promo_id}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[status]}`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {(p.tenants || []).map((t) => (
                                <span key={t} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {(p.surfaces || []).map((s) => (
                                <span key={s} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{s}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">{window}</td>
                          <td className="px-6 py-4">
                            <Link to={`/admin/promotions/${encodeURIComponent(p.promo_id)}/leads`}
                                  className="inline-flex items-center gap-1 text-sm font-medium text-zebu-600 dark:text-zebu-400 hover:underline">
                              <ListChecks className="h-4 w-4" />
                              {p.lead_count ?? 0}
                            </Link>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button onClick={() => setEditing(p)} className="p-1.5 rounded text-gray-500 hover:text-zebu-600 hover:bg-gray-100 dark:hover:bg-gray-700" title="Edit">
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleToggle(p)} className="p-1.5 rounded text-gray-500 hover:text-amber-600 hover:bg-gray-100 dark:hover:bg-gray-700" title={p.is_active ? 'Disable' : 'Enable'}>
                                <Power className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDelete(p)} className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <PromotionEditDialog
        open={creatingNew || editing !== null}
        initial={editing}
        onClose={() => { setEditing(null); setCreatingNew(false); }}
        onSaved={handleSaved}
      />
    </StandardLayout>
  );
};

export default PromotionsAdminPage;
